import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Lock, Database, Eye, Trash2, Mail } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[hsl(var(--accent))]" />
            Our Commitment to Your Privacy
          </CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm dark:prose-invert max-w-none">
          <p>
            At Zero Deficit Sync, we take your privacy seriously. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our mobile application and services.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-[hsl(var(--accent))]" />
            Information We Collect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">1. Personal Information</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Email address (for account creation and communication)</li>
              <li>Name (optional, for personalization)</li>
              <li>Profile information (business details, if provided)</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">2. Financial Data</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Bank transaction data (via Plaid integration)</li>
              <li>Receipt images and data</li>
              <li>Expense categorizations</li>
              <li>Mileage logs and trip information</li>
              <li>Tax-related documents</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">3. Device Information</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Device type and operating system</li>
              <li>Browser type and version</li>
              <li>IP address</li>
              <li>Push notification tokens</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">4. Usage Data</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>App usage patterns and features accessed</li>
              <li>Error logs and crash reports</li>
              <li>Performance metrics</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-[hsl(var(--accent))]" />
            How We Use Your Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
            <li>To provide and maintain our expense tracking and tax services</li>
            <li>To sync your financial transactions via Plaid</li>
            <li>To process receipts using OCR technology</li>
            <li>To categorize and organize your expenses</li>
            <li>To send you push notifications (with your consent)</li>
            <li>To improve our app's functionality and user experience</li>
            <li>To provide customer support</li>
            <li>To comply with legal obligations</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[hsl(var(--accent))]" />
            Data Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            We implement industry-standard security measures to protect your personal information:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>End-to-end encryption for data transmission (HTTPS/TLS)</li>
            <li>Encrypted database storage</li>
            <li>Secure OAuth 2.0 authentication via Google</li>
            <li>Plaid's bank-level security for financial connections</li>
            <li>Regular security audits and updates</li>
            <li>Limited employee access to user data</li>
          </ul>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            <strong>Note:</strong> No method of transmission over the internet is 100% secure. While we strive
            to protect your data, we cannot guarantee absolute security.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-[hsl(var(--accent))]" />
            Third-Party Services
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">We use the following third-party services:</p>
          
          <div className="space-y-3">
            <div className="p-3 border rounded-lg">
              <h4 className="font-semibold text-sm">Plaid</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Bank connection and transaction syncing. Plaid's privacy policy: 
                <a href="https://plaid.com/legal/#consumers" target="_blank" rel="noopener noreferrer" 
                   className="text-[hsl(var(--accent))] hover:underline ml-1">
                  plaid.com/legal
                </a>
              </p>
            </div>
            
            <div className="p-3 border rounded-lg">
              <h4 className="font-semibold text-sm">Google Cloud (Gemini)</h4>
              <p className="text-xs text-muted-foreground mt-1">
                OCR processing for receipt scanning. Google's privacy policy:
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                   className="text-[hsl(var(--accent))] hover:underline ml-1">
                  policies.google.com/privacy
                </a>
              </p>
            </div>
            
            <div className="p-3 border rounded-lg">
              <h4 className="font-semibold text-sm">Google Maps API</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Distance calculation for mileage tracking.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-[hsl(var(--accent))]" />
            Your Rights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Under GDPR (EU) and CCPA (California), you have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Erasure:</strong> Request deletion of your account and all data (via Settings)</li>
            <li><strong>Data Portability:</strong> Export your data in JSON or ZIP format (via Settings)</li>
            <li><strong>Objection:</strong> Object to certain data processing activities</li>
            <li><strong>Withdraw Consent:</strong> Opt-out of notifications or data collection</li>
          </ul>
          <p className="text-sm mt-4">
            <strong>To exercise these rights, go to Settings → Data Backup or contact us at:</strong>
            <a href="mailto:privacy@zerodeficitflow.com" className="text-[hsl(var(--accent))] hover:underline ml-1">
              privacy@zerodeficitflow.com
            </a>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[hsl(var(--accent))]" />
            Contact Us
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            If you have questions about this Privacy Policy or our data practices, contact us:
          </p>
          <div className="space-y-2 text-sm">
            <p><strong>Email:</strong> <a href="mailto:privacy@zerodeficitflow.com" className="text-[hsl(var(--accent))] hover:underline">privacy@zerodeficitflow.com</a></p>
            <p><strong>Support:</strong> <a href="mailto:support@zerodeficitflow.com" className="text-[hsl(var(--accent))] hover:underline">support@zerodeficitflow.com</a></p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            <strong>Changes to This Policy:</strong> We may update this Privacy Policy from time to time.
            We will notify you of significant changes via email or app notification. Your continued use
            of the app after changes constitutes acceptance of the updated policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
